import { useState } from "react";
import { Calendar, FileText, User, Tag, ArrowUpDown, Download } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/App";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { descargarDocumento } from "@/lib/descargarDocumento";

function EventosTimeline({ eventos, documentos, onSelectEvento, casoId }) {
  const [ordenPor, setOrdenPor] = useState("fecha"); // "fecha" o "hecho"

  if (eventos.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
        <p>No hay eventos registrados</p>
        <p className="text-sm mt-2">Agrega el primer evento para crear la línea de tiempo</p>
      </div>
    );
  }

  // Ordenar eventos según selección
  const eventosOrdenados = [...eventos].sort((a, b) => {
    if (ordenPor === "fecha") {
      return new Date(a.fecha_evento) - new Date(b.fecha_evento);
    } else {
      return a.numero_hecho - b.numero_hecho;
    }
  });

  const getTipoColor = (tipo) => {
    const colores = {
      correo: 'bg-blue-100 text-blue-800 border-blue-300',
      reunion: 'bg-purple-100 text-purple-800 border-purple-300',
      llamada: 'bg-green-100 text-green-800 border-green-300',
      pago_incompleto: 'bg-red-100 text-red-800 border-red-300',
      acuerdo_incumplido: 'bg-orange-100 text-orange-800 border-orange-300',
      otro: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colores[tipo] || colores.otro;
  };

  const getDocumentosRelacionados = (eventoId) => {
    return documentos.filter(doc => doc.evento_hecho_id === eventoId);
  };

  const handleDownloadDocumento = async (e, doc) => {
    e.stopPropagation(); // Evitar que se abra el evento al hacer click en descargar
    await descargarDocumento(casoId, doc);
  };

  return (
    <div className="space-y-4" data-testid="eventos-timeline">
      {/* Selector de ordenamiento */}
      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-blue-900">Ordenar eventos por:</span>
        </div>
        <Select value={ordenPor} onValueChange={setOrdenPor}>
          <SelectTrigger className="w-64 bg-white" data-testid="orden-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fecha">
              📅 Fecha del Hecho (Cronológico)
            </SelectItem>
            <SelectItem value="hecho">
              🔢 Número de Hecho
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {eventosOrdenados.map((evento, index) => {
        const docsRelacionados = getDocumentosRelacionados(evento.id);
        const fecha = new Date(evento.fecha_evento);
        
        return (
          <div key={evento.id} className="relative" data-testid={`timeline-event-${evento.id}`}>
            {/* Línea vertical conectora */}
            {index < eventosOrdenados.length - 1 && (
              <div className="absolute left-6 top-16 bottom-0 w-0.5 bg-blue-200 z-0"></div>
            )}
            
            {/* Círculo indicador */}
            <div className="absolute left-3.5 top-8 w-5 h-5 rounded-full bg-blue-600 border-4 border-white z-10 shadow-md"></div>
            
            {/* Contenido del evento */}
            <Card 
              className="ml-14 cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-600"
              onClick={() => onSelectEvento(evento)}
              data-testid={`timeline-card-${evento.id}`}
            >
              <CardContent className="p-4">
                {/* Encabezado */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="font-bold text-blue-900 bg-blue-50">
                        HECHO {evento.numero_hecho}
                      </Badge>
                      {evento.autor_tipo === "ia" ? (
                        <Badge className="bg-violet-100 text-violet-800 border border-violet-300" title={`Aprobado por ${evento.aprobado_por || ""} el ${String(evento.fecha_aprobacion || "").slice(0, 10)}`} data-testid={`autoria-badge-${evento.id}`}>
                          IA · {evento.autor_nombre || "Claude"}
                        </Badge>
                      ) : evento.autor_nombre ? (
                        <Badge variant="secondary" className="text-gray-600" data-testid={`autoria-badge-${evento.id}`}>
                          {evento.autor_nombre}
                        </Badge>
                      ) : null}
                      <Badge className={getTipoColor(evento.tipo_evento)}>
                        {evento.tipo_evento.replace('_', ' ')}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {evento.titulo_evento}
                    </h3>
                  </div>
                  
                  {/* Fecha en badge */}
                  <div className="text-right">
                    <Badge variant="secondary" className="whitespace-nowrap">
                      <Calendar className="h-3 w-3 mr-1" />
                      {fecha.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Badge>
                  </div>
                </div>

                {/* Tipificación */}
                {evento.tipificacion && (
                  <div className="flex items-center gap-2 mb-2 text-sm text-gray-600">
                    <Tag className="h-4 w-4" />
                    <span className="font-medium">{evento.tipificacion}</span>
                  </div>
                )}

                {/* Personas concurrentes */}
                {evento.personas_concurrentes && (
                  <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
                    <User className="h-4 w-4" />
                    <span>{evento.personas_concurrentes}</span>
                  </div>
                )}

                {/* Relato (preview) */}
                <div className="mb-3">
                  <p className="text-gray-700 text-sm line-clamp-3">
                    {evento.relato_usuario}
                  </p>
                </div>

                {/* Documentos relacionados */}
                {docsRelacionados.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-semibold text-gray-700">
                        {docsRelacionados.length} Documento{docsRelacionados.length > 1 ? 's' : ''} Adjunto{docsRelacionados.length > 1 ? 's' : ''}:
                      </span>
                    </div>
                    <div className="space-y-2">
                      {docsRelacionados.map(doc => (
                        <div 
                          key={doc.id} 
                          className="flex items-start gap-2 text-sm bg-blue-50 p-2 rounded-md hover:bg-blue-100 transition-colors"
                          data-testid={`doc-badge-${doc.id}`}
                        >
                          <FileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-blue-900">
                              Doc N° {doc.numero_documento}: {doc.nombre_archivo_original}
                            </p>
                            {doc.archivo_existe === false && (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 border border-red-300 rounded px-1.5 py-0.5 mt-1" data-testid={`archivo-perdido-badge-${doc.id}`}>
                                <AlertTriangle className="h-3 w-3" />
                                Archivo perdido — resubir
                              </span>
                            )}
                            {doc.descripcion_breve && (
                              <p className="text-gray-600 text-xs mt-0.5 truncate">
                                {doc.descripcion_breve}
                              </p>
                            )}
                            {doc.cita_relevante && (
                              <p className="text-gray-700 text-xs mt-1 italic">
                                "{doc.cita_relevante.substring(0, 100)}..."
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDownloadDocumento(e, doc)}
                            className="flex-shrink-0 h-8 px-2 hover:bg-blue-200"
                            data-testid={`download-doc-timeline-${doc.id}`}
                            title="Descargar documento"
                          >
                            <Download className="h-4 w-4 text-blue-600" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  );
}

export default EventosTimeline;
